import type { Gender } from '@hc-frontend/core-types';
import {
  dateToMonthDayYearString,
  tobaccoUseToTobaccoText,
} from '@hc-frontend/core-types-transformers';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { auditLead } from '@hc-frontend/services';

import type { AuditRepository } from '../../domain';

function parseGenderToDto(gender: Gender | undefined) {
  return gender === 'FEMALE' ? 'f' : 'm';
}

export type AuditFactoryParams = {
  baseUrl: string;
};

export function auditFactory({ baseUrl }: AuditFactoryParams): AuditRepository {
  return {
    auditLead: async (prospect) => {
      try {
        const { cookies } = await import('next/headers');
        const result = await auditLead(
          baseUrl,
          {
            f_name: prospect.applicant?.firstName,
            l_name: prospect.applicant?.lastName,
            email: prospect.contactInformation.email,
            address1: prospect.contactInformation.address,
            city: prospect.location?.city,
            state: prospect.location?.state,
            gender: parseGenderToDto(prospect.applicant?.gender),
            dob: prospect.applicant?.dateOfBirth
              ? dateToMonthDayYearString(prospect.applicant.dateOfBirth)
              : undefined,
            phone1: prospect.contactInformation.phoneNumber,
            smoker: prospect.applicant?.tobaccoUse
              ? tobaccoUseToTobaccoText(prospect.applicant.tobaccoUse)
              : undefined,
            tcpa_universal_id: prospect.leadId,
            zip: prospect.location?.zipCode,
          },
          {
            headers: {
              'snowplow-data': (await cookies())
                .getAll()
                .find((cookie) => cookie.name.startsWith('_sp_id'))
                ?.value as string,
            },
          },
        );

        if (result.result !== 'success') {
          throw new Error(`Error auditing lead with code ${result.status}`);
        }

        return createSuccessOperationResult(undefined);
      } catch (e) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToAuditLead',
              rootCause: e as Error,
            },
          ],
        });
      }
    },
  };
}
